/**
 * Cross-border scheme profiles, as the Operator Console and the portal read
 * them. A profile summary comes from the ISO engine (ProfileSummary); these
 * helpers turn it into what a page shows and what a form sends.
 */

/** How far a profile can be relied on, in words and as a status tone. */
export const PROFILE_STATUS = {
  REFERENCE:   { label: 'Sandbox reference', tone: 'info',
    note: "The Sandbox's own profile, built on ISO 20022 and FATF practice. It is no real scheme's." },
  PROVISIONAL: { label: 'Provisional', tone: 'warn',
    note: 'Built from partial or unofficial sources and not reviewed by the scheme operator. Expect it to change.' },
  REVIEWED:    { label: 'Reviewed', tone: 'ok',
    note: 'Reviewed by the scheme operator.' },
  PINNED:      { label: 'Pinned', tone: 'ok',
    note: 'A reviewed version kept fixed for a certification campaign.' },
};

/** The status shown for a profile, with a neutral fallback for one this portal does not know. */
export function profileStatus(status) {
  return PROFILE_STATUS[status] || { label: status || 'Unknown', tone: null, note: '' };
}

/** A scheme's name without the status the badge beside it already shows. */
export function shortSchemeName(summary) {
  return (summary?.name || '').replace(/,\s*(provisional|reviewed|pinned)\s*$/i, '').trim();
}

const REGION_NAMES = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' }); } catch { return null; }
})();

/** Names the browser gives in a form no one writes ("Congo - Kinshasa"). */
const COUNTRY_NAMES = {
  CD: 'Democratic Republic of the Congo',
  CG: 'Republic of the Congo',
};

/** A country's English name from its ISO code ("RW" → "Rwanda"); the code itself when it has none. */
export function countryName(code) {
  if (!code) return '';
  if (COUNTRY_NAMES[code]) return COUNTRY_NAMES[code];
  try { return REGION_NAMES?.of(code) || code; } catch { return code; }
}

/** A name's acronym, the part before its long form: "SADC (Southern African…)" → "SADC", "TCIB (Transactions…)" → "TCIB". */
export function acronymOf(region) {
  return (region || '').replace(/\s*\(.*\)\s*$/, '').trim();
}

/** "Rwanda (RW) · RWF" */
export function homeLabel(home) {
  return home ? `${countryName(home.country)} (${home.country}) · ${home.currency}` : '';
}

/**
 * A scheme's corridors read as its members: each country with its currency and
 * the most one payment from it may send, and whether every member can pay every
 * other (a regional scheme), in which case a page lists the members rather than
 * every pair.
 */
export function membersOf(corridors) {
  const list = corridors || [];
  const members = [];
  const byCountry = new Map();
  for (const c of list) {
    for (const [country, currency] of [[c.from, c.send], [c.to, c.receive]]) {
      if (!byCountry.has(country)) {
        const m = { country, currency, maxSend: null };
        byCountry.set(country, m);
        members.push(m);
      }
    }
    const sender = byCountry.get(c.from);
    if (sender.maxSend == null && c.maxSend != null) sender.maxSend = c.maxSend;
  }
  const pairs = new Set(list.map((c) => `${c.from}>${c.to}>${c.send}>${c.receive}`));
  const complete = members.length >= 2 && list.length === members.length * (members.length - 1)
    && members.every((a) => members.every((b) => a === b
      || pairs.has(`${a.country}>${b.country}>${a.currency}>${b.currency}`)));
  return { members, complete };
}

/** The key a home is chosen by in a form: "RW/RWF". */
export function homeKey(home) {
  return home ? `${home.country}/${home.currency}` : '';
}

/** Whether the profile identifies each participant by a member id (PAPSS does). */
export function needsMemberId(summary) {
  return Boolean(summary?.wire?.memberIdPattern);
}

/**
 * What is wrong with a member id under this profile, in words, or null when it
 * will do. The engine has the last word; this only saves a round trip.
 */
export function memberIdProblem(summary, memberId) {
  const id = (memberId || '').trim();
  const pattern = summary?.wire?.memberIdPattern;
  if (!pattern) return null;
  if (!id) return 'This scheme identifies each participant by a member id: enter one.';
  let re;
  try { re = new RegExp(`^(?:${pattern})$`); } catch { return null; }
  return re.test(id) ? null : `Not in the scheme's form (${pattern}).`;
}

/**
 * The body the platform API takes, from what the form holds. Country and
 * currency are upper-cased, a blank member id is none.
 */
export function assignmentBody({ memberId, home, roles }) {
  const [country, currency] = String(home || '').split('/');
  const id = (memberId || '').trim();
  return {
    memberId: id || null,
    homeCountry: (country || '').trim().toUpperCase(),
    homeCurrency: (currency || '').trim().toUpperCase(),
    roles: roles || [],
  };
}

/**
 * Every scheme the operator might look for when assigning one, in the order the
 * form lists them: those the participant can take, then those it already holds,
 * then those still coming. Only the first can be chosen; the others say why not,
 * so a scheme never seems to have vanished.
 */
export function schemeChoices(profiles, assigned, comingSoon) {
  const held = new Set((assigned || []).map((a) => a.profileId));
  const served = profiles || [];
  return [
    ...served.filter((p) => !held.has(p.id))
      .map((p) => ({ id: p.id, label: `${p.name} (${p.ref})`, disabled: false })),
    ...served.filter((p) => held.has(p.id))
      .map((p) => ({ id: p.id, label: `${p.name}: already assigned, change it in the list above`, disabled: true })),
    ...(comingSoon || [])
      .map((s) => ({ id: s.id, label: `${s.name}: coming soon`, disabled: true })),
  ];
}

/** Whether the profile's FX model has participants convert, where one can act as FX provider for others. */
export function offersFxProviderRole(summary) {
  const model = summary?.fx?.model;
  return model === 'PARTICIPANTS_CONVERT' || model === 'FXP_CONVERSION';
}

/** What each part of a profile means, in words, keyed by the engine's value. */
export const LABELS = {
  envelope: {
    BAH_DOCUMENT: 'Business message: the AppHdr and a <Document>, as on the domestic switch',
    MONTRAN_MESSAGE: 'Message envelope: the AppHdr beside the message body, with no <Document>',
  },
  signature: {
    BAH_THREE_REFERENCE: 'Three references: the header, the document and the key information',
    ENVELOPED_WHOLE_MESSAGE: 'One enveloped reference over the whole message, C14N 1.1 inside, RSA-SHA256',
  },
  transport: {
    PUSH: 'You call the scheme; the scheme calls your callback endpoints',
    POLL: 'You call the scheme; you poll an inbox for what it sends you, and confirm each message',
  },
  fx: {
    NONE: 'No conversion: one currency end to end',
    SCHEME_CONVERTS: 'The scheme converts: you pay in your currency, the receiver is paid in theirs',
    PARTICIPANTS_CONVERT: 'Participants convert: the scheme clears in one currency',
    FXP_CONVERSION: 'An FX provider converts, as a separate leg',
  },
  liquidity: {
    PREFUNDED: 'Prefunded: a position per currency, checked before each payment',
    NET_DEBIT_CAP: 'A net debit cap per currency',
    NONE: 'Not checked',
  },
  settlement: {
    DEFERRED_NET: 'Deferred net, at settlement windows',
    GROSS: 'Gross, payment by payment',
  },
  level: {
    GROUP: 'for the whole message (GrpSts)',
    TRANSACTION: 'per transaction (TxSts)',
  },
};

/** The words for a value, or the value itself when the portal has none. */
export function label(kind, value) {
  return (LABELS[kind] && LABELS[kind][value]) || value || '—';
}

/** Where a participant reaches the scheme: the profile's own base path. */
export function schemeBase(profileId) {
  return `/api/switch/iso20022/xb/${profileId}`;
}

/**
 * The endpoints a participant uses under the profile, in the order a
 * participant meets them. Each is { method, path, what }.
 */
export function endpointsFor(summary) {
  if (!summary) return [];
  const b = schemeBase(summary.id);
  const o = summary.offers || {};
  const list = [];
  if (o.polls) {
    list.push(
      { method: 'POST', path: `${b}/papss/Message`, what: 'Send a payment (pacs.008), a status request (pacs.028) or your answer to an incoming payment (pacs.002), signed in its envelope. Answered with a receipt.' },
      { method: 'GET',  path: `${b}/papss/Message`, what: 'Poll your inbox: one message per call, with X-PAPSSRTP-MessageSeq, -MessageType, -RemainingOutputs and -PossibleDuplicate.' },
      { method: 'POST', path: `${b}/papss/MessageAck`, what: 'Confirm a polled message by its X-PAPSSRTP-MessageSeq: Stored or NotFound. An incoming payment is confirmed by your answer instead.' },
      { method: 'POST', path: `${b}/papss/sign`, what: 'Sign a message as your institution with its key in the Sandbox; a <Document> is first put in the envelope from your member id.' },
    );
  } else {
    list.push(
      { method: 'POST', path: `${b}/clearing/credit`, what: 'Send a signed payment (pacs.008). Answered with the signed pacs.002.' },
      { method: 'POST', path: `${b}/clearing/status-request`, what: 'Ask after a payment (pacs.028): its stored status, or NOOR when the scheme never received it.' },
      { method: 'POST', path: `${b}/incoming/status`, what: 'Post your signed answer (pacs.002) to an incoming payment, when you do not answer in the callback response.' },
    );
  }
  if (o.quotes) {
    list.push(
      { method: 'POST', path: `${b}/fx/quotes`, what: 'Ask for an FX quote: { to, basis, amount, uetr }.' },
      { method: 'POST', path: `${b}/fx/quotes/{quoteId}/accept`, what: summary.fx?.quotesLock ? 'Accept a quote: the rate is locked for that payment.' : 'Accept a quote. Rates are indicative: the scheme prices the payment itself.' },
    );
  }
  list.push({ method: 'POST', path: `${b}/incoming/trigger`, what: 'Have the Sandbox send you a payment for a scenario, to test your receiving side.' });
  if (o.liquidity) list.push({ method: 'GET', path: `${b}/liquidity`, what: 'Your positions per currency.' });
  if (o.statements) list.push({ method: 'GET', path: `${b}/settlement/statements`, what: 'Your camt.053 statements.' });
  if (o.statementRequest) list.push({ method: 'POST', path: `${b}/reporting/statement-request`, what: 'Ask for statements with a signed camt.060.' });
  list.push({ method: 'GET', path: `${b}/profile`, what: 'This profile in full, its rules included.' });
  return list;
}

/** What a profile rule checks, in words: "required", "one of INST", "at most 140 characters"… */
export function ruleCheck(rule) {
  if (!rule) return '';
  const when = rule.when
    ? ` (when ${rule.when.path}${rule.when.equals != null ? ` is ${rule.when.equals}`
      : rule.when.present === false ? ' is absent' : ' is present'})`
    : '';
  const what = {
    REQUIRED: 'required',
    FORBIDDEN: 'must be absent',
    EQUALS: `must be ${rule.value}`,
    CODE_LIST: `one of ${(rule.codes || []).join(', ')}`,
    MAX_LENGTH: `at most ${rule.maxLength} characters`,
    PATTERN: `in the form ${rule.pattern}`,
    STRUCTURED_ADDRESS: 'a structured address (town and country at least)',
  }[rule.check] || String(rule.check || '').toLowerCase();
  return what + when;
}

/** Incoming-payment scenarios, in words. */
export const SCENARIOS = {
  ACCEPT: 'A complete payment to an account you hold',
  UNKNOWN_ACCOUNT: 'To an account no one holds',
  MISSING_ORIGINATOR_DATA: "Without the originator's address",
  SCREENING_CONFIRMED: 'The originator is a confirmed watch-list match',
  SCREENING_HOLD_RELEASE: 'The originator is a potential match; your review releases it',
  SCREENING_HOLD_REJECT: 'The originator is a potential match; your review rejects it',
};

/** Where a case stands, in words and as a tone. */
export const CASE_STATES = {
  AWAITING: { label: 'Awaiting your answer', tone: 'info' },
  HELD: { label: 'Held: final answer due', tone: 'warn' },
  PASS: { label: 'Passed', tone: 'ok' },
  FAIL: { label: 'Failed', tone: 'danger' },
};

/** Whether a case is still open, so its page should look again. */
export function caseOpen(c) {
  return c?.state === 'AWAITING' || c?.state === 'HELD';
}
